import React, { memo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PipelineStage, ExpertiseData } from '../../types/pipeline.types';
import { StagesGrid } from '../StagesGrid';
import { ExpertiseList } from '@/ui/components/molecules/ExpertiseList';
import { ExpertiseStatus } from '@/ui/components/molecules/ExpertiseCard/ExpertiseCard.types';
import { PipelineControls } from '@/ui/components/molecules/PipelineControls';
import { usePipelineStages } from '../../hooks/usePipelineStages';
import { useStageFilters } from '../../hooks/useStageFilters';
import { transformExpertiseForCard } from '../../utils/pipelineHelpers';
import { SORT_OPTIONS, FILTER_OPTIONS, STAGE_DEFAULT_SORT } from '../../constants/stages.constants';
import './PipelineStages.styles.css';

interface PipelineStagesProps {
  stages: PipelineStage[];
  expertises: ExpertiseData[];
}

const PipelineStagesComponent: React.FC<PipelineStagesProps> = ({
  stages,
  expertises
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine the base path based on current location
  const basePath = location.pathname.includes('/bureau') ? '/bureau' : '/admin';
  
  // Stage management
  const {
    selectedStageId,
    selectedStage,
    stageCounts,
    urgentCounts,
    stageExpertises,
    handleStageSelect
  } = usePipelineStages(expertises);

  // Filtering and sorting
  const {
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    activeFilter,
    setActiveFilter,
    filteredExpertises,
    resultsCount,
    clearSearchAndFilters
  } = useStageFilters(stageExpertises, selectedStageId ?? undefined);

  // Clear filters when stage changes and set default sort
  useEffect(() => {
    if (selectedStageId !== null) {
      clearSearchAndFilters();
      // Set default sort for specific stages
      if (STAGE_DEFAULT_SORT[selectedStageId]) {
        setSortBy(STAGE_DEFAULT_SORT[selectedStageId]);
      }
    }
  }, [selectedStageId, clearSearchAndFilters, setSortBy]);

  // Handle expertise click - navigate to tracker detail
  const handleExpertiseClick = (expertise: ExpertiseData) => {
    const affaireId = expertise.id || expertise.reference;

    navigate(`${basePath}/tracker/${affaireId}`, {
      state: {
        from: `${basePath}/pipeline`,
      }
    });
  };

  // Handle FileMaker button click
  const handleFileMakerClick = (expertise: ExpertiseData) => {
    const fmUrl = `fmp://$/fm2.chatelainxpert.be/ChatelainXpert.fmp12?script=mondaytofm&param=${expertise.id}`;
    window.location.href = fmUrl;
  };

  // Transform expertises for card display and add necessary props
  const transformedExpertises = filteredExpertises.map(expertise => {
    const transformed = transformExpertiseForCard(expertise);
    
    
    return {
      id: transformed.id,
      reference: transformed.reference || `REF-${transformed.id}`,
      title: transformed.type_expertise || transformed.type || 'Expertise immobilière',
      status: (transformed.isUrgent ? 'urgent' : 'en_cours') as ExpertiseStatus,
      statusDisplay: expertise.status || transformed.status || 'status not found', // Pass raw backend status
      stageId: expertise.stage || transformed.stage || selectedStageId || undefined,
      clientName: transformed.client_name,
      expertName: transformed.expert,
      createdAt: transformed.date || transformed.date_demande,
      dueDate: transformed.date_du_rv,
      address: (() => {
        // Handle address as array of strings from API
        if (Array.isArray(transformed.address) && transformed.address.length > 0) {
          // First element is usually street, second is "postalCode city"
          const [street, cityLine] = transformed.address;
          let city = '';
          let postalCode = '';
          
          if (cityLine) {
            // Try to extract postal code and city from second element
            const match = cityLine.match(/^(\d{4,5})\s+(.+)$/);
            if (match) {
              postalCode = match[1];
              city = match[2];
            } else {
              city = cityLine;
            }
          }
          
          return { street, city, postalCode };
        }
        
        // Fallback to old field structure if available
        return {
          street: transformed.add_street ? 
            `${transformed.add_number || ''} ${transformed.add_street}`.trim() : 
            undefined,
          city: transformed.add_locality,
          postalCode: transformed.add_postcode
        };
      })(),
      amount: transformed.price ? parseFloat(transformed.price) : undefined,
      paid: transformed.affaire_facturee === 'emise' ||
            transformed.facture_emise === 'oui' ||
            transformed.paymentInfo?.affaire_facturee === 'Oui',
      perception: transformed.perception,
      invoiceStatus: typeof transformed.affaire_facturee === 'string' ? transformed.affaire_facturee : undefined,
      invoiceIssued: transformed.facture_emise,
      // Don't pass progress - let ExpertiseCard calculate from stageId
      priority: (transformed.isUrgent ? 'urgent' : 'medium') as 'low' | 'medium' | 'high' | 'urgent',
      tags: transformed.tags,
      responsableName: transformed.responsable_name,
      // Store original data for click handlers
      _originalData: expertise
    };
  });

  return (
    <div className="pipeline-stages">
      {/* All content in scrollable area */}
      <div className="pipeline-content">
        {/* Stages Container - now inside scrollable content */}
        <div className="stages-wrapper">
          <StagesGrid
            stages={stages}
            counts={stageCounts}
            urgentCounts={urgentCounts}
            expertises={expertises}
            selectedStageId={selectedStageId}
            onStageSelect={handleStageSelect}
          />
        </div>

        {/* Selected Stage Content */}
        {selectedStage && (
          <>
            {/* Controls */}
            <PipelineControls
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              sortOrder={sortOrder}
              onSortOrderChange={setSortOrder}
              sortOptions={SORT_OPTIONS}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              filterOptions={FILTER_OPTIONS}
              showFilters={true}
              resultsCount={resultsCount}
              searchPlaceholder="Rechercher dans les expertises..."
            />

          {/* Expertise List */}
          <ExpertiseList
            expertises={transformedExpertises}
            onExpertiseClick={(expertise) => {
              // Use the original data stored in _originalData
              const originalData = (expertise as any)._originalData;
              handleExpertiseClick(originalData);
            }}
            onFileMakerClick={(expertise) => {
              // Use the original data stored in _originalData
              const originalData = (expertise as any)._originalData;
              handleFileMakerClick(originalData);
            }}
            className="pipeline-expertise-list"
            showFacadeImage={true}
            showProgress={true}
            showActions={true}
            isSearchActive={!!searchQuery.trim()}
            emptyStateTitle={`Aucune expertise dans cette étape`}
            emptyStateMessage={`Il n'y a actuellement aucune expertise à l'étape "${selectedStage.label}".`}
          />
          </>
        )}
      </div>
    </div>
  );
};

export const PipelineStages = memo(PipelineStagesComponent);